import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Collapse,
  Container,
  Divider,
  Group,
  Modal,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Timeline,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconArrowBack,
  IconArrowDown,
  IconArrowsSort,
  IconArrowUp,
  IconCamera,
  IconCameraOff,
  IconChevronDown,
  IconChevronRight,
  IconDownload,
  IconPackage,
  IconFileSpreadsheet,
  IconRefresh,
  IconSearch,
  IconScan,
  IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { DeviceHistory, RmaListEntry, RmaStatus } from "../api/types";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { DefectsTab } from "../components/rma/DefectsTab";
import { MoveToReturnBoxModal } from "../components/rma/MoveToReturnBoxModal";
import { ExportToSheetModal } from "../components/shipments/ExportToSheetModal";
import { ReturnBoxesTab } from "../components/rma/ReturnBoxesTab";
import { useCurrentUser } from "../hooks/useCurrentUser";

const DEDUPE_MS = 2500;

export function Rma() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const canEdit = user.role === "admin" || user.role === "operator";

  const [manualValue, setManualValue] = useState("");
  const [cameraOn, setCameraOn] = useState(false);
  const [history, setHistory] = useState<DeviceHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionOpen, setActionOpen] = useState<RmaStatus | null>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rmaList, setRmaList] = useState<RmaListEntry[]>([]);
  const [rmaOpen, setRmaOpen] = useState(true);
  const [rmaLoading, setRmaLoading] = useState(false);
  const [candidates, setCandidates] = useState<RmaListEntry[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [sheetAllOpen, setSheetAllOpen] = useState(false);

  type RmaSortKey = "dev_eui" | "model" | "status" | "shipment_code" | "customer_name" | "updated_at";
  type SortDir = "asc" | "desc";
  const [sortKey, setSortKey] = useState<RmaSortKey>("updated_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [rmaFilter, setRmaFilter] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  function toggleSort(key: RmaSortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "updated_at" ? "desc" : "asc");
    }
  }

  const displayedRmaList = useMemo(() => {
    const q = rmaFilter.trim().toLowerCase();
    const filtered = q
      ? rmaList.filter((d) =>
          [d.dev_eui, d.model, d.serial_number, d.shipment_code, d.customer_name, d.status, d.notes]
            .some((f) => (f ?? "").toString().toLowerCase().includes(q)))
      : rmaList;
    const dir = sortDir === "asc" ? 1 : -1;
    const sortable = [...filtered];
    sortable.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortKey) {
        case "dev_eui": av = a.dev_eui; bv = b.dev_eui; break;
        case "model": av = a.model ?? ""; bv = b.model ?? ""; break;
        case "status": av = a.status; bv = b.status; break;
        case "shipment_code": av = a.shipment_code ?? ""; bv = b.shipment_code ?? ""; break;
        case "customer_name": av = a.customer_name ?? ""; bv = b.customer_name ?? ""; break;
        case "updated_at": av = a.updated_at ?? -Infinity; bv = b.updated_at ?? -Infinity; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sortable;
  }, [rmaList, sortKey, sortDir, rmaFilter]);

  const pageCount = Math.max(1, Math.ceil(displayedRmaList.length / pageSize));
  // Keep the page in range when the filtered set / page size shrinks.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);
  const pagedRmaList = useMemo(
    () => displayedRmaList.slice((page - 1) * pageSize, page * pageSize),
    [displayedRmaList, page, pageSize],
  );

  function RmaSortHeader({ k, label }: { k: RmaSortKey; label: string }) {
    const isActive = sortKey === k;
    const Icon = isActive ? (sortDir === "asc" ? IconArrowUp : IconArrowDown) : IconArrowsSort;
    return (
      <UnstyledButton
        onClick={() => toggleSort(k)}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontWeight: 600 }}
      >
        {label}
        <Icon size={12} style={{ opacity: isActive ? 1 : 0.4 }} />
      </UnstyledButton>
    );
  }

  async function loadRmaList() {
    setRmaLoading(true);
    try {
      // All RMA modules in the project (returned / defect / restored), whether in
      // a return box or not — the search tab is a full, filterable list.
      const res = await api.listDevicesByStatus(["returned", "defect", "restored"], 1000);
      setRmaList(res.devices);
      // Drop selections whose IDs no longer exist in the list
      setSelected((prev) => {
        const ids = new Set(res.devices.map((d) => d.id));
        const next = new Set<number>();
        for (const id of prev) if (ids.has(id)) next.add(id);
        return next;
      });
    } catch {
      // ignore
    } finally {
      setRmaLoading(false);
    }
  }

  function toggleSelected(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    // Select/clear the currently VISIBLE (filtered) rows.
    const visibleIds = displayedRmaList.map((d) => d.id);
    setSelected((prev) => {
      const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      return allVisibleSelected ? new Set() : new Set(visibleIds);
    });
  }

  async function exportSelected(format: "xlsx" | "csv") {
    if (selected.size === 0) return;
    setExporting(true);
    try {
      const ids = Array.from(selected);
      const { blob, filename } = await api.exportDevices(ids, format);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      notifications.show({
        color: "green",
        message: t("rma.exportedToast", { count: ids.length, format: format.toUpperCase() }),
      });
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("rma.exportFailedTitle"),
        message: detail.slice(0, 200),
      });
    } finally {
      setExporting(false);
    }
  }

  async function doRevert() {
    if (selected.size === 0) return;
    setReverting(true);
    try {
      const ids = Array.from(selected);
      const res = await api.revertDevicesFromRma(ids);
      notifications.show({
        color: res.failed.length === 0 ? "green" : "yellow",
        message: t("rma.revertedToast", {
          reverted: res.reverted.length,
          failed: res.failed.length,
        }),
      });
      if (res.failed.length > 0) {
        for (const f of res.failed.slice(0, 3)) {
          notifications.show({ color: "red", message: `#${f.id}: ${f.error}` });
        }
      }
      setRevertOpen(false);
      setSelected(new Set());
      void loadRmaList();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({ color: "red", title: t("rma.saveFailedTitle"), message: detail.slice(0, 200) });
    } finally {
      setReverting(false);
    }
  }

  useEffect(() => {
    void loadRmaList();
  }, []);

  const lastScanRef = useRef<{ raw: string; ts: number } | null>(null);
  const manualInputRef = useRef<HTMLInputElement>(null);

  async function lookupAndLoad(raw: string) {
    if (!raw) return;
    const now = Date.now();
    if (
      lastScanRef.current &&
      lastScanRef.current.raw === raw &&
      now - lastScanRef.current.ts < DEDUPE_MS
    ) {
      return;
    }
    lastScanRef.current = { raw, ts: now };

    setLoading(true);
    setCandidates([]);
    try {
      const result = await api.lookupDevice(raw);
      if (result.found && result.device) {
        const h = await api.deviceHistory(result.device.id);
        setHistory(h);
        return;
      }
      // Exact lookup failed — fall back to partial search (model/serial/eui fragment)
      const trimmed = raw.trim();
      if (trimmed.length >= 2) {
        const res = await api.searchDevices(trimmed);
        if (res.devices.length === 1) {
          const h = await api.deviceHistory(res.devices[0].id);
          setHistory(h);
          return;
        }
        if (res.devices.length > 1) {
          setHistory(null);
          setCandidates(res.devices);
          return;
        }
      }
      notifications.show({ color: "red", message: t("rma.devEuiUnknown", { key: result.dev_eui ?? raw }) });
      setHistory(null);
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("rma.lookupErrorTitle"),
        message: detail.slice(0, 200),
      });
    } finally {
      setLoading(false);
    }
  }

  async function loadCandidate(id: number) {
    setLoading(true);
    try {
      const h = await api.deviceHistory(id);
      setHistory(h);
      setCandidates([]);
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("rma.lookupErrorTitle"),
        message: detail.slice(0, 200),
      });
    } finally {
      setLoading(false);
    }
  }

  function handleManualSubmit() {
    const v = manualValue.trim();
    setManualValue("");
    if (v) void lookupAndLoad(v);
    manualInputRef.current?.focus();
  }

  function openAction(status: RmaStatus) {
    setActionOpen(status);
    setReason("");
  }

  async function submitAction() {
    if (!history?.device || !actionOpen) return;
    setSubmitting(true);
    try {
      await api.markDeviceReturned(history.device.id, actionOpen, reason);
      notifications.show({
        color: actionOpen === "returned" ? "yellow" : actionOpen === "restored" ? "teal" : "red",
        message: t("rma.markedToast", { status: actionOpen.toUpperCase() }),
      });
      setActionOpen(null);
      // Reload history + RMA list to show updated status + new audit entry
      const h = await api.deviceHistory(history.device.id);
      setHistory(h);
      void loadRmaList();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("rma.saveFailedTitle"),
        message: detail.slice(0, 200),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container size="lg">
      <Title order={2} mb="md">
        {t("rma.headerTitle")}
      </Title>

      <Tabs defaultValue="search">
        <Tabs.List mb="md">
          <Tabs.Tab value="search">{t("rma.tabSearch")}</Tabs.Tab>
          <Tabs.Tab value="defects">{t("rma.tabDefects")}</Tabs.Tab>
          <Tabs.Tab value="boxes">{t("rma.tabBoxes")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="search">

      <Paper withBorder p="md" mb="md">
        <Group align="flex-end" wrap="wrap" gap="sm">
          <TextInput
            ref={manualInputRef}
            label={t("rma.scanLabel")}
            placeholder={t("rma.scanPlaceholder")}
            size="md"
            value={manualValue}
            onChange={(e) => setManualValue(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleManualSubmit();
            }}
            autoFocus
            leftSection={<IconScan size={16} />}
            style={{ flex: "1 1 280px" }}
            rightSection={
              <Button size="xs" onClick={handleManualSubmit} disabled={!manualValue.trim()}>
                {t("rma.searchButton")}
              </Button>
            }
            rightSectionWidth={70}
          />
          <Switch
            label={cameraOn ? t("scanner.cameraOn") : t("scanner.cameraOff")}
            checked={cameraOn}
            onChange={(e) => setCameraOn(e.currentTarget.checked)}
            thumbIcon={cameraOn ? <IconCamera size={12} /> : <IconCameraOff size={12} />}
          />
        </Group>
        {cameraOn && (
          <div style={{ width: "100%", maxWidth: 480, marginTop: 12 }}>
            <BarcodeScanner
              active={cameraOn}
              onDetect={(raw) => void lookupAndLoad(raw)}
            />
          </div>
        )}
      </Paper>

      {candidates.length > 0 && (
        <Paper withBorder p="md" mb="md">
          <Title order={5} mb="xs">
            {t("rma.candidatesTitle", { count: candidates.length })}
          </Title>
          <ResponsiveTable
            mobile={{
              rows: candidates,
              rowKey: (d) => d.id,
              title: (d) => d.serial_number ?? d.dev_eui.toLowerCase(),
              subtitle: (d) => `${d.model ?? "—"} · ${d.customer_name ?? "—"}`,
              badge: (d) => <Badge color={statusColor(d.status)} variant="light">{d.status}</Badge>,
              fields: [{ label: "DEV_EUI", render: (d) => <Text ff="monospace" size="sm">{d.dev_eui.toLowerCase()}</Text> }],
              onOpen: (d) => void loadCandidate(d.id),
            }}
          >
          <Table.ScrollContainer minWidth={600}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>DEV_EUI</Table.Th>
                  <Table.Th>{t("rma.colModel")}</Table.Th>
                  <Table.Th>{t("rma.colSerial")}</Table.Th>
                  <Table.Th>{t("rma.colStatus")}</Table.Th>
                  <Table.Th>{t("rma.colCustomer")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {candidates.map((d) => (
                  <Table.Tr
                    key={d.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => void loadCandidate(d.id)}
                  >
                    <Table.Td ff="monospace">{d.dev_eui.toLowerCase()}</Table.Td>
                    <Table.Td>{d.model ?? "—"}</Table.Td>
                    <Table.Td>{d.serial_number ?? "—"}</Table.Td>
                    <Table.Td>
                      <Badge color={statusColor(d.status)} variant="light">
                        {d.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{d.customer_name ?? "—"}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          </ResponsiveTable>
        </Paper>
      )}

      {loading && <Text c="dimmed" mb="md">{t("common.loading")}</Text>}

      {history?.device && (
        <Stack gap="md" mb="md">
          <Card withBorder>
            <Group justify="space-between" mb="xs" wrap="wrap">
              <div>
                <Text size="xs" c="dimmed">
                  DEV_EUI
                </Text>
                <Text ff="monospace" size="lg" fw={600}>
                  {history.device.dev_eui.toLowerCase()}
                </Text>
              </div>
              <Badge size="lg" color={statusColor(history.device.status)} variant="filled">
                {history.device.status}
              </Badge>
            </Group>
            <Divider my="sm" />
            <Group gap="xl" wrap="wrap">
              <InfoBlock label={t("rma.infoModel")} value={history.device.model ?? t("common.none")} />
              <InfoBlock label={t("rma.infoSerial")} value={history.device.serial_number ?? t("common.none")} />
              <InfoBlock
                label={t("rma.infoBatch")}
                value={
                  history.batch ? (
                    <Anchor component={Link} to={`/batches/${history.batch.id}`}>
                      {history.batch.code}
                    </Anchor>
                  ) : (
                    t("common.none")
                  )
                }
              />
              {history.shipment ? (
                <>
                  <InfoBlock
                    label={t("rma.infoShipment")}
                    value={
                      <Anchor component={Link} to={`/shipments/${history.shipment.id}`}>
                        {history.shipment.code}
                      </Anchor>
                    }
                  />
                  <InfoBlock label={t("rma.infoCustomer")} value={history.customer?.name ?? t("common.none")} />
                  <InfoBlock label={t("rma.infoLocation")} value={history.shipment.location ?? t("common.none")} />
                  <InfoBlock label={t("rma.infoBranch")} value={history.shipment.branch ?? t("common.none")} />
                  <InfoBlock
                    label={t("rma.infoBox")}
                    value={history.device.box_number ?? t("common.none")}
                  />
                  <InfoBlock
                    label={t("rma.infoShippedDate")}
                    value={history.shipment.shipped_date ?? t("common.none")}
                  />
                </>
              ) : (
                <>
                  {/* Never in a shipment: one "Not shipped" instead of five
                      dashes for shipment, customer, location, branch and date. */}
                  <InfoBlock label={t("rma.infoShipment")} value={t("rma.notShipped")} />
                  {history.device.box_number && (
                    <InfoBlock label={t("rma.infoBox")} value={history.device.box_number} />
                  )}
                </>
              )}
              <InfoBlock
                label={t("rma.infoFlashedAt")}
                value={
                  history.device.flashed_at ? (
                    new Date(history.device.flashed_at).toLocaleString()
                  ) : history.flash_summary && history.flash_summary.failed > 0 ? (
                    <Text span size="sm" c="red">
                      {t("rma.flashFailedSummary", {
                        n: history.flash_summary.failed,
                        date: history.flash_summary.last_failed_at
                          ? new Date(history.flash_summary.last_failed_at).toLocaleString()
                          : "—",
                      })}
                    </Text>
                  ) : (
                    t("common.none")
                  )
                }
              />
            </Group>

            {history.device.notes && (
              <>
                <Divider my="sm" />
                <Text size="xs" c="dimmed" mb={4}>
                  {t("rma.deviceNotes")}
                </Text>
                <Code block fz="xs">
                  {history.device.notes}
                </Code>
              </>
            )}

            {canEdit && (
              <>
                <Divider my="sm" />
                <Group gap="xs" wrap="wrap">
                  <Button
                    color="yellow"
                    leftSection={<IconArrowBack size={16} />}
                    onClick={() => openAction("returned")}
                    disabled={history.device.status === "returned"}
                  >
                    {t("rma.markReturned")}
                  </Button>
                  <Button
                    color="red"
                    leftSection={<IconAlertTriangle size={16} />}
                    onClick={() => openAction("defect")}
                    disabled={history.device.status === "defect"}
                  >
                    {t("rma.markDefect")}
                  </Button>
                  <Button
                    color="teal"
                    variant="light"
                    onClick={() => setActionOpen("restored")}
                    disabled={
                      history.device.status !== "returned" &&
                      history.device.status !== "defect"
                    }
                  >
                    {t("rma.markRestored")}
                  </Button>
                </Group>
              </>
            )}
          </Card>

          <Card withBorder>
            <Title order={5} mb="sm">
              {t("rma.historyTitle", { count: history.timeline.length })}
            </Title>
            {history.timeline.length === 0 ? (
              <Text c="dimmed" size="sm">
                {t("rma.noHistory")}
              </Text>
            ) : (
              <ScrollArea h={400} type="auto">
                <Timeline active={history.timeline.length} bulletSize={20} lineWidth={2}>
                  {history.timeline.map((entry) => (
                    <Timeline.Item
                      key={entry.id}
                      title={
                        <Group gap="xs">
                          <Badge size="sm" variant="light" color={actionColor(entry.action)}>
                            {entry.action}
                          </Badge>
                          {entry.username && (
                            <Text size="xs" c="dimmed">
                              {entry.username}
                            </Text>
                          )}
                        </Group>
                      }
                    >
                      <Text size="xs" c="dimmed" mb={4}>
                        {new Date(entry.created_at).toLocaleString()}
                      </Text>
                      {entry.payload != null && (
                        <Code block fz="xs">
                          {JSON.stringify(entry.payload, null, 2)}
                        </Code>
                      )}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </ScrollArea>
            )}
          </Card>
        </Stack>
      )}

      {!loading && !history && candidates.length === 0 && (
        <Alert color="gray" variant="light" mb="md">
          {t("rma.scanToSeeHistory")}
        </Alert>
      )}

      <Paper withBorder p="md" mb="md">
        <Group justify="space-between" mb="xs">
          <Group
            gap="xs"
            style={{ cursor: "pointer" }}
            onClick={() => setRmaOpen((v) => !v)}
          >
            {rmaOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            <Title order={5}>{t("rma.allRmaDevices")}</Title>
            <Badge color="yellow" variant="light">
              {t("rma.returnedBadge", { count: rmaList.filter((d) => d.status === "returned").length })}
            </Badge>
          </Group>
          <Group gap="xs">
            {selected.size > 0 && (
              <>
                <Text size="xs" c="dimmed">
                  {t("rma.selectedCount", { count: selected.size })}
                </Text>
                <Button
                  size="xs"
                  leftSection={<IconDownload size={12} />}
                  loading={exporting}
                  onClick={() => exportSelected("xlsx")}
                >
                  {t("rma.exportXlsx")}
                </Button>
                <Button
                  size="xs"
                  variant="default"
                  leftSection={<IconDownload size={12} />}
                  loading={exporting}
                  onClick={() => exportSelected("csv")}
                >
                  {t("rma.exportCsv")}
                </Button>
                <Button
                  size="xs"
                  color="blue"
                  leftSection={<IconPackage size={12} />}
                  onClick={() => setMoveOpen(true)}
                >
                  {t("rma.bulkMoveToBox")}
                </Button>
                <Button
                  size="xs"
                  color="red"
                  variant="light"
                  leftSection={<IconTrash size={12} />}
                  onClick={() => setRevertOpen(true)}
                >
                  {t("rma.bulkRevert")}
                </Button>
              </>
            )}
            <Button
              size="xs"
              color="green"
              variant="light"
              leftSection={<IconFileSpreadsheet size={12} />}
              onClick={() => setSheetAllOpen(true)}
            >
              {t("rma.exportAllToSheet")}
            </Button>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconRefresh size={12} />}
              onClick={loadRmaList}
              loading={rmaLoading}
            >
              {t("rma.reload")}
            </Button>
          </Group>
        </Group>
        <Collapse in={rmaOpen}>
          <Group mb="sm" gap="sm" align="center" wrap="wrap">
            <TextInput
              leftSection={<IconSearch size={14} />}
              placeholder={t("rma.filterPlaceholder")}
              value={rmaFilter}
              onChange={(e) => { setRmaFilter(e.currentTarget.value); setPage(1); }}
              style={{ flex: "1 1 320px" }}
            />
            <Select
              w={110}
              data={["30", "50", "100"]}
              value={String(pageSize)}
              onChange={(v) => { setPageSize(Number(v) || 50); setPage(1); }}
              aria-label={t("rma.perPage")}
              allowDeselect={false}
            />
            <Text size="sm" c="dimmed">
              {t("rma.filterCount", { shown: displayedRmaList.length, total: rmaList.length })}
            </Text>
          </Group>
          {rmaList.length === 0 ? (
            <Text c="dimmed" size="sm">
              {t("rma.noRmaDevices")}
            </Text>
          ) : displayedRmaList.length === 0 ? (
            <Text c="dimmed" size="sm">
              {t("rma.filterNoMatch", { q: rmaFilter })}
            </Text>
          ) : (
            <ResponsiveTable
              stickyColumns={2}
              mobile={{
                rows: pagedRmaList,
                sort: {
                  options: [
                    { value: "status", label: t("rma.colStatus") },
                    { value: "dev_eui", label: "DEV_EUI" },
                    { value: "model", label: t("rma.colModel") },
                    { value: "shipment_code", label: t("rma.colShipment") },
                    { value: "customer_name", label: t("rma.colCustomer") },
                    { value: "updated_at", label: t("rma.colUpdated") },
                  ],
                  value: sortKey,
                  dir: sortDir,
                  onChange: (v, d) => (v === sortKey ? setSortDir(d) : toggleSort(v as RmaSortKey)),
                },
                rowKey: (d) => d.id,
                title: (d) => d.serial_number ?? d.dev_eui.toLowerCase(),
                subtitle: (d) => `${d.model ?? t("common.none")} · ${d.customer_name ?? t("common.none")}`,
                badge: (d) => (
                  <Badge size="sm" color={d.status === "defect" ? "red" : "yellow"} variant="light">
                    {d.status}
                  </Badge>
                ),
                fields: [
                  { label: "DEV_EUI", render: (d) => <Text ff="monospace" size="sm">{d.dev_eui.toLowerCase()}</Text> },
                  { label: t("rma.colShipment"), render: (d) => d.shipment_code ?? t("common.none") },
                  { label: t("rma.colReason"), render: (d) => firstNoteLine(d.notes) ?? t("common.none") },
                  { label: t("rma.colUpdated"), render: (d) => (d.updated_at ? new Date(d.updated_at).toLocaleString() : t("common.none")) },
                ],
                onOpen: (d) => void lookupAndLoad(d.dev_eui),
                selection: {
                  isSelected: (d) => selected.has(d.id),
                  toggle: (d) => toggleSelected(d.id),
                  allSelected: displayedRmaList.length > 0 && displayedRmaList.every((d) => selected.has(d.id)),
                  someSelected: selected.size > 0,
                  toggleAll: toggleSelectAll,
                },
              }}
            >
            <Table.ScrollContainer minWidth={760}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={36}>
                      <Checkbox
                        checked={displayedRmaList.length > 0 && displayedRmaList.every((d) => selected.has(d.id))}
                        indeterminate={selected.size > 0 && !displayedRmaList.every((d) => selected.has(d.id))}
                        onChange={toggleSelectAll}
                        aria-label={t("rma.selectAll")}
                      />
                    </Table.Th>
                    <Table.Th w={90}><RmaSortHeader k="status" label={t("rma.colStatus")} /></Table.Th>
                    <Table.Th><RmaSortHeader k="dev_eui" label="DEV_EUI" /></Table.Th>
                    <Table.Th><RmaSortHeader k="model" label={t("rma.colModel")} /></Table.Th>
                    <Table.Th><RmaSortHeader k="shipment_code" label={t("rma.colShipment")} /></Table.Th>
                    <Table.Th><RmaSortHeader k="customer_name" label={t("rma.colCustomer")} /></Table.Th>
                    <Table.Th>{t("rma.colReason")}</Table.Th>
                    <Table.Th w={140}><RmaSortHeader k="updated_at" label={t("rma.colUpdated")} /></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pagedRmaList.map((d) => (
                    <Table.Tr key={d.id}>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.has(d.id)}
                          onChange={() => toggleSelected(d.id)}
                          aria-label={t("rma.selectOne", { eui: d.dev_eui })}
                        />
                      </Table.Td>
                      <Table.Td
                        style={{ cursor: "pointer" }}
                        onClick={() => void lookupAndLoad(d.dev_eui)}
                      >
                        <Badge
                          size="sm"
                          color={d.status === "defect" ? "red" : "yellow"}
                          variant="light"
                        >
                          {d.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td
                        style={{ cursor: "pointer" }}
                        onClick={() => void lookupAndLoad(d.dev_eui)}
                      >
                        <Text ff="monospace" size="sm">
                          {d.dev_eui.toLowerCase()}
                        </Text>
                      </Table.Td>
                      <Table.Td
                        style={{ cursor: "pointer" }}
                        onClick={() => void lookupAndLoad(d.dev_eui)}
                      >
                        <Text size="sm">{d.model ?? t("common.none")}</Text>
                      </Table.Td>
                      <Table.Td
                        style={{ cursor: "pointer" }}
                        onClick={() => void lookupAndLoad(d.dev_eui)}
                      >
                        {d.shipment_code ? (
                          <Text size="sm">{d.shipment_code}</Text>
                        ) : (
                          <Text size="sm" c="dimmed">
                            {t("common.none")}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td
                        style={{ cursor: "pointer" }}
                        onClick={() => void lookupAndLoad(d.dev_eui)}
                      >
                        <Text size="sm">{d.customer_name ?? t("common.none")}</Text>
                      </Table.Td>
                      <Table.Td
                        style={{ cursor: "pointer" }}
                        onClick={() => void lookupAndLoad(d.dev_eui)}
                      >
                        <Text size="xs" c="dimmed" lineClamp={1} maw={260}>
                          {firstNoteLine(d.notes) ?? t("common.none")}
                        </Text>
                      </Table.Td>
                      <Table.Td
                        style={{ cursor: "pointer" }}
                        onClick={() => void lookupAndLoad(d.dev_eui)}
                      >
                        <Text size="xs" c="dimmed">
                          {d.updated_at ? new Date(d.updated_at).toLocaleString() : t("common.none")}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            </ResponsiveTable>
          )}
          {pageCount > 1 && (
            <Group justify="center" mt="sm">
              <Pagination
                total={pageCount}
                value={page}
                onChange={setPage}
                size="sm"
                withEdges
              />
            </Group>
          )}
        </Collapse>
      </Paper>

      <Modal
        opened={actionOpen !== null}
        onClose={() => setActionOpen(null)}
        title={
          actionOpen === "returned"
            ? t("rma.markReturnedTitle")
            : actionOpen === "defect"
              ? t("rma.markDefectTitle")
              : actionOpen === "restored"
                ? t("rma.markRestoredTitle")
                : ""
        }
      >
        <Stack>
          <Textarea
            label={t("rma.reasonLabel")}
            placeholder={
              actionOpen === "defect"
                ? t("rma.reasonPlaceholderDefect")
                : t("rma.reasonPlaceholderReturned")
            }
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            autosize
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setActionOpen(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              color={actionOpen === "defect" ? "red" : actionOpen === "restored" ? "teal" : "yellow"}
              onClick={submitAction}
              loading={submitting}
              leftSection={<IconTrash size={16} />}
            >
              {t("common.confirm")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={revertOpen}
        onClose={() => setRevertOpen(false)}
        title={t("rma.revertConfirmTitle")}
        centered
      >
        <Stack>
          <Text size="sm">
            {t("rma.revertConfirmBody", { count: selected.size })}
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRevertOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              loading={reverting}
              leftSection={<IconTrash size={16} />}
              onClick={() => void doRevert()}
            >
              {t("common.confirm")}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <MoveToReturnBoxModal
        opened={moveOpen}
        onClose={() => setMoveOpen(false)}
        deviceIds={Array.from(selected)}
        onMoved={() => {
          setSelected(new Set());
          void loadRmaList();
        }}
      />
      <ExportToSheetModal
        opened={sheetAllOpen}
        onClose={() => setSheetAllOpen(false)}
        target="all_returns"
      />
        </Tabs.Panel>
        <Tabs.Panel value="defects">
          <DefectsTab />
        </Tabs.Panel>
        <Tabs.Panel value="boxes">
          <ReturnBoxesTab />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}

function InfoBlock({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{value}</Text>
    </div>
  );
}

function firstNoteLine(notes: string | null | undefined): string | null {
  if (!notes) return null;
  const line = notes.split(/\r?\n/, 1)[0]?.trim();
  return line || null;
}

function statusColor(status: string): string {
  if (status === "shipped" || status === "delivered") return "green";
  if (status === "defect") return "red";
  if (status === "returned") return "yellow";
  if (status === "restored") return "teal";
  if (status === "flashed" || status === "tested") return "violet";
  if (status === "labeled" || status === "assigned") return "blue";
  return "gray";
}

function actionColor(action: string): string {
  if (action.startsWith("device.flash") || action.startsWith("firmware")) return "violet";
  if (action.startsWith("device.returned")) return "yellow";
  if (action.startsWith("device.defect")) return "red";
  if (action.startsWith("device")) return "cyan";
  if (action.startsWith("shipment")) return "teal";
  if (action.startsWith("batch")) return "indigo";
  return "gray";
}
